import React from "react";
import { DecoratorNode, DOMConversionMap, DOMConversionOutput, DOMExportOutput, EditorConfig, LexicalEditor, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical"
import ImageEditor from "./Toolbar/ImageEditor";

export type ImagePayload = {
  src: string,
  altText: string,
  link?: string,
  width?: string,
  height?: string,
}
type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    link?: string;
    width?: string;
    height?: string;
  },
  SerializedLexicalNode
>;
class ImageNode extends DecoratorNode<JSX.Element> {
  private __image_payload: ImagePayload;

  constructor(src: string, altText: string, link?: string, width?: string, height?: string, key?: NodeKey) {
    super(key);
    this.__image_payload = {
      src,
      altText,
      link,
      width,
      height
    }
  }

  // serialization from JSON
  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, link, width, height } = serializedNode;
    return $createImageNode({
      src,
      altText,
      link,
      width,
      height
    });
  }

  // serialization to JSON
  exportJSON(): SerializedImageNode {
    return {
      type: 'custom-img-node',
      version: 1,
      src: this.__image_payload.src,
      link: this.__image_payload.link,
      altText: this.__image_payload.altText,
      width: this.__image_payload.width,
      height: this.__image_payload.height
    };
  }

  // HTML -> node (load)
  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({ conversion: convertImageElement, priority: 0 }),
      a: (domNode: HTMLElement) => {
        const anchor = domNode as HTMLAnchorElement;
        const isImageOnlyAnchor = anchor.children.length === 1 && anchor.children[0].tagName === 'IMG';
        if (!isImageOnlyAnchor) return null;
        return { conversion: () => ({ node: null }), priority: 2 };
      }
    }
  }

  // node -> HTML (save)
  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const img = document.createElement('img');
    img.setAttribute('src', this.__image_payload.src);
    img.setAttribute('alt', this.__image_payload.altText);

    const height: string | undefined = this.__image_payload.height;
    const width: string | undefined = this.__image_payload.width;
    if (width) img.setAttribute('width', width);
    if (height) img.setAttribute('height', height);

    const link: string | undefined = this.__image_payload.link;
    if (link) {
      img.setAttribute('data-href', link);
      const a = document.createElement('a');
      a.setAttribute('href', link);
      a.appendChild(img);
      return { element: a };
    }

    return { element: img };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    return document.createElement('div');
  }
  updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: EditorConfig): boolean {
    return false;
  }

  // JSX Element that is rendered in LexicalEditor
  decorate(_editor: LexicalEditor, _config: EditorConfig): JSX.Element | null {
    return <ImageEditor imagePayload={this.__image_payload} nodeKey={this.__key} />
  }

  resize(width?: string, height?: string) {
    const writable = this.getWritable();
    writable.__image_payload = { ...writable.__image_payload, width, height };
  }

  setLink(link: string | undefined) {
    const writable = this.getWritable();
    writable.__image_payload = { ...writable.__image_payload, link };
  }

  getLink(): string | undefined {
    return this.getLatest().__image_payload.link;
  }

  isInline(): boolean {
    return false;
  }
  static getType(): string {
    return 'custom-img-node';
  }
  static clone(node: ImageNode): ImageNode {
    return $createImageNode(node.__image_payload, node.__key);
  }
}

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (!(domNode instanceof HTMLImageElement)) return null;
  const src = domNode.getAttribute('src') ?? '';
  const altText = domNode.getAttribute('alt') ?? '';
  const link = domNode.getAttribute('data-href') ?? domNode.closest('a')?.getAttribute('href') ?? undefined;
  const width = domNode.getAttribute('width') ?? '';
  const height = domNode.getAttribute('height') ?? '';
  const node = $createImageNode({ src, altText, link, width, height });
  return { node };
}

export function $createImageNode(payload: ImagePayload, key?: NodeKey): ImageNode {
  return new ImageNode(payload.src, payload.altText, payload.link, payload.width, payload.height, key)
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

export default ImageNode
